/**
 * Sol des zones de surface (phase E) : relief procédural déterministe (bruit de valeur fBm), plateaux arasés.
 * La même grille de hauteurs sert au champ de hauteurs de collision (Rapier) et au maillage du rendu.
 */
import type { ZoneData } from '../../data';

export type GroundData = NonNullable<ZoneData['ground']>;

export interface GroundField {
  /** Sommets par côté. */
  n: number;
  size: number;
  /** Hauteurs (m) en ordre colonne : index = ligne (z) + colonne (x) × n, comme le champ de hauteurs Rapier. */
  heights: Float32Array;
}

function hash(x: number, z: number, seed: number): number {
  let h = (x * 374761393 + z * 668265263 + seed * 1442695041) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function noise(x: number, z: number, seed: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  const s = (t: number) => t * t * (3 - 2 * t);
  const xf = s(x - xi), zf = s(z - zi);
  const a = hash(xi, zi, seed), b = hash(xi + 1, zi, seed), c = hash(xi, zi + 1, seed), d = hash(xi + 1, zi + 1, seed);
  return a + (b - a) * xf + (c - a) * zf + (a - b - c + d) * xf * zf;
}

function fbm(x: number, z: number, seed: number): number {
  let v = 0, amp = 0.5, f = 1;
  for (let o = 0; o < 5; o++) {
    v += amp * noise(x * f, z * f, seed + o * 31);
    amp *= 0.5;
    f *= 2.07;
  }
  return v / 0.97;
}

/** Hauteur du relief en (x, z), plateaux compris (fonction continue). */
export function groundHeight(g: GroundData, x: number, z: number): number {
  // Collines douces et crêtes plus marquées.
  const hills = fbm(x / 520 + 13.1, z / 520 - 7.3, g.seed) * 2 - 1;
  const ridges = 1 - Math.abs(fbm(x / 260 - 3.7, z / 260 + 5.1, g.seed + 101) * 2 - 1);
  let h = g.baseY + g.amplitude * (0.75 * hills + 0.45 * (ridges - 0.6));
  for (const flat of g.flats) {
    const d = Math.hypot(x - flat.center[0], z - flat.center[1]);
    const t = d <= flat.radius * 0.7 ? 1 : d >= flat.radius ? 0 : 1 - (d - flat.radius * 0.7) / (flat.radius * 0.3);
    h += (flat.height - h) * (t * t * (3 - 2 * t));
  }
  return h;
}

/** Position (x, z) du sommet (ligne, colonne) de la grille. */
export function groundVertex(field: GroundField, row: number, col: number): [number, number] {
  const step = field.size / (field.n - 1);
  return [-field.size / 2 + col * step, -field.size / 2 + row * step];
}

export function groundField(g: GroundData): GroundField {
  const n = g.resolution + 1;
  const field: GroundField = { n, size: g.size, heights: new Float32Array(n * n) };
  for (let col = 0; col < n; col++) {
    for (let row = 0; row < n; row++) {
      const [x, z] = groundVertex(field, row, col);
      field.heights[row + col * n] = groundHeight(g, x, z);
    }
  }
  return field;
}
